import moment from "moment-timezone";
import { range } from "./fn";

const TIME_PICKER_MAX_TIME = {
  hour: "9",
  ampm: "pm"
};

const autocorrectValue = (value, options) => {
  if (options && options.length > 0) {
    return typeof options[0] === "object"
      ? options.some(v => v.value === value)
        ? value
        : options[0].value
      : options.includes(value)
      ? value
      : options[0];
  }
};

// Filters and returns minutes[0, 15, 30, 45] greater than soonest minute
// soonest minute is addition of moment.now , scheduled delay(3600) and adding a rounding off of 15 mins
// Example: if soonest minute 15. returns [15,30,45]
export const minutesGreaterThanSoonestMinutes = (minutes, tmp) =>
  minutes.filter(n => n >= tmp);

// Returns only minutes[0, 15, 30, 45] greater than soonest minute if present else returns [0, 15, 30, 45]
export const minHourMinutes = (minHourOption, soonest) => {
  const minutes = [0, 15, 30, 45];
  if (soonest.hour() - minHourOption <= 1) {
    const soonestMinute = soonest.minute();
    const minMinutes = minutesGreaterThanSoonestMinutes(minutes, soonestMinute);
    return minMinutes;
  } else {
    return minutes;
  }
};

// Returns true if the value is minHour
export const isItTimePickerMinTime = (minHour, valueMinutes) =>
  valueMinutes.hour === minHour.hour || valueMinutes.__hour === minHour.hour;

export const isItTimePickerMaxTime = values =>
  (values.hour === TIME_PICKER_MAX_TIME.hour ||
    values.__hour === TIME_PICKER_MAX_TIME.hour) &&
  (values.ampm === TIME_PICKER_MAX_TIME.ampm ||
    values.__ampm === TIME_PICKER_MAX_TIME.ampm);

export const updateTimePickerOptionsAndValues = (
  min_hour = 7,
  max_hour = 21,
  delaySchedule = 3600,
  time_zone,
  { type = "OND", ampm = "am", hour = 0, minute = 0 } = {},
  settings = {},
  params = ""
) => {
  const options = {
    type: [],
    ampm: [],
    hour: [],
    minute: []
  };
  const values = {};

  const TYPE_OPTIONS = [
    { value: "SCH", text: settings?.translations?.today },
    { value: "SCH+1", text: settings?.translations?.tomorrow }
  ];

  if (!params || params.includes("TDFieldOptionNextAvailable")) {
    TYPE_OPTIONS.unshift({ value: "OND", text: settings?.translations?.asap });
  }

  const now = moment();

  const soonest = moment(now);
  soonest.add(delaySchedule, "seconds"); // add the schedule delay
  soonest.add(30 - (soonest.minute() % 15), "minutes"); // round up to the nearest 15-minute mark

  if (time_zone) {
    now.tz(time_zone);
    soonest.tz(time_zone);
  }

  // Type (ASAP, today or tomorrow)
  options.type = [
    ...TYPE_OPTIONS.filter(
      option =>
        !(
          (soonest.hour() >= max_hour || soonest.date() !== now.date()) &&
          option.value === "SCH"
        )
    )
  ];
  values.type = autocorrectValue(type, options.type);

  if (values.type !== "OND") {
    // There are two possible cases where the schedule delay should be taken into a count:
    // either the current day is selected or the delay is big enough to affect the next day.
    const delayAffectsDay =
      values.type === "SCH" || soonest.date() !== now.date();

    // AM/PM
    if (min_hour >= 12 || (delayAffectsDay && soonest.hour() >= 12)) {
      options.ampm = ["pm"];
    } else if (max_hour <= 12) {
      options.ampm = ["am"];
    } else {
      options.ampm = ["am", "pm"];
    }
    values.ampm = autocorrectValue(ampm, options.ampm);

    // Hours
    const minHourOption = Math.max(
      min_hour,
      values.ampm === "am" ? 0 : 12,
      delayAffectsDay ? soonest.hour() : 0
    );
    const maxHourOption = Math.min(max_hour, values.ampm === "am" ? 12 : 24);

    options.hour = range(minHourOption, maxHourOption, true)
      .map(hourItem => hourItem % 12)
      .map(hourItem => (hourItem === 0 ? 12 : hourItem))
      .map(hourItem => hourItem.toString());
    values.hour = autocorrectValue(hour, options.hour);

    // Minutes
    const delayAffectsHour =
      (parseInt(values.hour, 10) % 12) + (values.ampm === "am" ? 0 : 12) ===
      soonest.hour();

    // Returns minutes[0] for maxHour
    // Returns only minutes greater on soonest.minutes
    // Returns minutes[0, 15, 30, 45]
    const minutesForAllHours = (minimimHour, soonestTime, val) => {
      if (isItTimePickerMinTime(minimimHour, val)) {
        return minHourMinutes(minimimHour, soonestTime);
      } else if (isItTimePickerMaxTime(val)) {
        return [0];
      } else {
        return [0, 15, 30, 45];
      }
    };

    options.minute = minutesForAllHours(minHourOption, soonest, values);

    options.minute = options.minute
      .filter(
        minuteItem =>
          !(
            delayAffectsDay &&
            delayAffectsHour &&
            minuteItem < soonest.minute()
          )
      )
      .map(minuteItem => `0${minuteItem}`.slice(-2));
    values.minute = autocorrectValue(minute, options.minute);
  }

  return [options, values];
};

const timePickerBuilder = {
  updateTimePickerOptionsAndValues
};

export default timePickerBuilder;
